import React, { useContext, useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import validator from "validator";
import { postcodeValidator } from "postcode-validator";

import { LoanFormContext } from "./LoanForm";
import FormStep from "../../FormStep";
import { InputError } from "../Controls/index";

const Address: React.FunctionComponent<{
  onSubmit?: (value: AddressType) => void;
}> = (props: { onSubmit?: (value: AddressType) => void }) => {
  const loanFormContext = useContext(LoanFormContext);
  const [address1, setAddress1] = useState<string>(
    loanFormContext.address ? loanFormContext.address.address1 : ""
  );
  const [zip, setZip] = useState<string>(
    loanFormContext.address ? loanFormContext.address.zip : ""
  );
  const [city, setCity] = useState<string>(
    loanFormContext.address ? loanFormContext.address.city : ""
  );
  const [state, setState] = useState<string>(
    loanFormContext.address ? loanFormContext.address.state : ""
  );
  const [cityError, setCityError] = useState<InputError>({
    error: "",
    touched: false,
  });
  const [stateError, setStateError] = useState<InputError>({
    error: "",
    touched: false,
  });
  const [zipError, setZipError] = useState<InputError>({
    error: "",
    touched: false,
  });
  const [address1Error, setAddress1Error] = useState<InputError>({
    error: "",
    touched: false,
  });

  useEffect(() => {
    if (validator.isEmpty(address1)) {
      setAddress1Error({ ...address1Error, error: "Address is required." });
    } else if (!validator.isLength(address1, { min: 3, max: 25 })) {
      setAddress1Error({ ...address1Error, error: "Enter a valid address." });
    } else {
      setAddress1Error({ ...address1Error, error: "" });
    }
  }, [address1]);

  useEffect(() => {
    if (validator.isEmpty(city)) {
      setCityError({ ...cityError, error: "City is required." });
    } else if (city.length < 3 || city.length > 24) {
      setCityError({ ...cityError, error: "Enter a valid city." });
    } else {
      setCityError({ ...cityError, error: "" });
    }
  }, [city]);

  useEffect(() => {
    if (validator.isEmpty(state)) {
      setStateError({ ...stateError, error: "State is required." });
    } else if (state.length < 3 || state.length > 24) {
      setStateError({ ...stateError, error: "Enter a valid state name." });
    } else {
      setStateError({ ...stateError, error: "" });
    }
  }, [state]);

  useEffect(() => {
    if (validator.isEmpty(zip)) {
      setZipError({ ...zipError, error: "Zip code is required." });
    } else if (!postcodeValidator(zip, "US")) {
      setZipError({ ...zipError, error: "Enter a valid zip code." });
    } else {
      setZipError({ ...zipError, error: "" });
    }
  }, [zip]);

  const handleSubmit = () => {
    if (!cityError.error || !zipError.error || !address1Error.error) {
      if (props.onSubmit) props.onSubmit({ city, zip, address1, state });
    } else {
      setAddress1Error({ ...address1Error, touched: true });
      setCityError({ ...cityError, touched: true });
      setZipError({ ...zipError, touched: true });
    }
  };

  return (
    <div>
      <FormStep
        header={
          <h1 className="heading-main">What is your current street address?</h1>
        }
      >
        <div className="form-inputs-wrapper">
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="Street address"
              value={address1}
              onBlur={() => {
                setAddress1Error({ ...address1Error, touched: true });
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setAddress1(e.target.value)
              }
            />
            {address1Error && address1Error.touched && (
              <div className="hint text-danger">{address1Error.error}</div>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="Enter zip"
              value={zip}
              onBlur={() => {
                setZipError({ ...zipError, touched: true });
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setZip(e.target.value)
              }
            />
            {zipError?.error && zipError.touched && (
              <div className="hint text-danger">{zipError.error}</div>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="City"
              value={city}
              onBlur={() => {
                setCityError({ ...cityError, touched: true });
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setCity(e.target.value)
              }
            />
            {cityError && cityError.touched && (
              <div className="hint text-danger">{cityError.error}</div>
            )}
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="State"
              value={state}
              onBlur={() => {
                setStateError({ ...stateError, touched: true });
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setState(e.target.value)
              }
            />
            {stateError && stateError.touched && (
              <div className="hint text-danger">{stateError.error}</div>
            )}
          </Form.Group>
          <div className="mt-5">
            <Button variant="primary" block onClick={handleSubmit}>
              Continue
            </Button>
          </div>
        </div>
      </FormStep>
    </div>
  );
};

export type AddressType = {
  address1: string;
  zip: string;
  city: string;
  state: string;
};

export default Address;
